import { useState, useEffect } from 'react'
import { Layout, Typography, Card } from 'antd'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import WeeklyCalendar from './components/WeeklyCalendar'
import TodoModal from './components/TodoModal'
import SettingsModal from './components/SettingsModal'
import SettingsButton from './components/SettingsButton'
import { todoService, settingsService } from './services/api'
import { Todo, Settings } from './types'
import { getWeekStart, timeToMinutes, minutesToTime, PIXELS_PER_15_MIN } from './utils/timeUtils'
import { getRandomColor } from './utils/colorUtils'
import 'antd/dist/reset.css'
import './styles/App.css'

const { Header, Content } = Layout
const { Title } = Typography

const App = () => {
  const [todos, setTodos] = useState<Todo[]>([])
  const [settings, setSettings] = useState<Settings>({
    font_size: 'medium',
    theme_color: 'rgb(24, 144, 255)',
    lunch_color: 'rgba(82, 196, 26, 0.08)'
  })
  const [selectedTodo, setSelectedTodo] = useState<Todo | null>(null)
  const [modalVisible, setModalVisible] = useState(false)
  const [settingsModalVisible, setSettingsModalVisible] = useState(false)
  const [modalPosition, setModalPosition] = useState<{ top: number; left: number } | null>(null)
  const [modalAnchorRect, setModalAnchorRect] = useState<{ top: number; bottom: number; left: number; right: number; width: number; height: number } | null>(null)
  const [weekStart, setWeekStart] = useState(getWeekStart())
  const [loading, setLoading] = useState(true)
  const [mutatingMap, setMutatingMap] = useState<Record<string, boolean>>({})

  const startMutating = (id: string) => {
    setMutatingMap((prev: Record<string, boolean>) => ({ ...prev, [id]: true }))
  }

  const stopMutating = (id: string) => {
    setMutatingMap((prev: Record<string, boolean>) => {
      const next = { ...prev }
      delete next[id]
      return next
    })
  }

  // Load todos for current week
  const loadTodos = async () => {
    try {
      setLoading(true)
      // 一次性清理所有 weekly_repeat 数据（本地标记避免重复执行）
      const cleanupKey = 'weeklyRepeatCleanupDone'
      if (typeof window !== 'undefined' && !window.localStorage.getItem(cleanupKey)) {
        try {
          await todoService.deleteAllWeeklyRepeats()
          window.localStorage.setItem(cleanupKey, '1')
        } catch (e) {
          console.error('Failed to cleanup weekly repeats:', e)
        }
      }

      const currentWeekTodos = await todoService.getCurrentWeekTodos(weekStart)
      setTodos(currentWeekTodos)
    } catch (error) {
      console.error('Failed to load todos:', error)
    } finally {
      setLoading(false)
    }
  }

  // Load settings
  const loadSettings = async () => {
    try {
      const userSettings = await settingsService.getSettings()
      if (userSettings) {
        // 合并远端设置与本地默认值，避免缺失字段（如 lunch_color）覆盖默认值
        setSettings(prev => ({ ...prev, ...userSettings }))
      }
    } catch (error) {
      console.error('Failed to load settings:', error)
    }
  }

  useEffect(() => {
    loadTodos()
    loadSettings()
  }, [weekStart])

  const handleCreateTodo = (dayOfWeek: number, startTime: string, element: HTMLElement) => {
    // Handle 24:00 edge case - convert to 00:00
    let adjustedStartTime = startTime
    if (startTime === "24:00") {
      adjustedStartTime = "00:00"
    }

    // Calculate position based on the click element
    const rect = element.getBoundingClientRect()
    const scrollX = window.pageXOffset || document.documentElement.scrollLeft
    const scrollY = window.pageYOffset || document.documentElement.scrollTop

    // Create a temporary todo object with selected day and time
    const tempTodo = {
      id: '',
      title: '',
      day_of_week: dayOfWeek,
      start_time: adjustedStartTime,
      end_time: minutesToTime(timeToMinutes(adjustedStartTime) + 15), // Default 15 minutes duration
      color: getRandomColor(), // Add random color
      weekly_repeat: false,
      completed: false,
      week_start_date: weekStart.toISOString().split('T')[0],
      created_at: '',
      updated_at: ''
    }
    setSelectedTodo(tempTodo as Todo)
    setModalPosition({
      top: rect.bottom + scrollY,
      left: rect.left + scrollX
    })
    setModalAnchorRect({
      top: rect.top + scrollY,
      bottom: rect.bottom + scrollY,
      left: rect.left + scrollX,
      right: rect.right + scrollX,
      width: rect.width,
      height: rect.height
    })
    setModalVisible(true)
  }

  const handleEditTodo = (todo: Todo, element: HTMLElement) => {
    // Calculate position based on click element
    const rect = element.getBoundingClientRect()
    const scrollX = window.pageXOffset || document.documentElement.scrollLeft
    const scrollY = window.pageYOffset || document.documentElement.scrollTop
    setSelectedTodo(todo)
    setModalPosition({
      top: rect.bottom + scrollY,
      left: rect.left + scrollX
    })
    setModalAnchorRect({
      top: rect.top + scrollY,
      bottom: rect.bottom + scrollY,
      left: rect.left + scrollX,
      right: rect.right + scrollX,
      width: rect.width,
      height: rect.height
    })
    setModalVisible(true)
  }

  // Close edit modal when calendar scrolls
  const handleCalendarScroll = () => {
    if (modalVisible) {
      setModalVisible(false)
      setModalPosition(null)
      setModalAnchorRect(null)
      setSelectedTodo(null)
    }
  }

  const handleSaveTodo = async (todoData: Omit<Todo, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      if (selectedTodo && selectedTodo.id) {
        // 编辑：仅更新当前项
        startMutating(selectedTodo.id)
        await todoService.updateTodo(selectedTodo.id, todoData)
        stopMutating(selectedTodo.id)
      } else {
        // 新建：仅创建当前项
        await todoService.createTodo(todoData)
      }
      await loadTodos()
      setModalVisible(false)
      setModalPosition(null)
    } catch (error) {
      console.error('Failed to save todo:', error)
      if (selectedTodo?.id) stopMutating(selectedTodo.id)
    }
  }

  const handleDeleteTodo = async (id: string, options?: { deleteAfterSeries?: boolean }) => {
    try {
      // 乐观删除：先从界面移除
      setTodos(prev => prev.filter(t => t.id !== id))
      setModalVisible(false)
      setModalPosition(null)
      const current = todos.find(t => t.id === id)
      if (!current) {
        await loadTodos()
        return
      }
      // 删除当前项（允许删除当前项，不受完成状态限制）
      await todoService.deleteTodo(id)
      // 若选择删除之后所有：删除未完成且当前及之后的同系列项（跨周）
      if (options?.deleteAfterSeries) {
        const seriesItems = await todoService.getTodosBySeriesKey({
          title: current.title,
          start_time: current.start_time,
          end_time: current.end_time,
          color: current.color
        })
        const future = seriesItems.filter(t =>
          !t.completed && (
            t.week_start_date > current.week_start_date ||
            (t.week_start_date === current.week_start_date && t.day_of_week > current.day_of_week)
          )
        )
        for (let i = 0; i < future.length; i++) {
          await todoService.deleteTodo(future[i].id)
        }
      }
      await loadTodos()
    } catch (error) {
      console.error('Failed to delete todo:', error)
      // 回滚（重新加载）
      await loadTodos()
    }
  }

  const handleToggleComplete = async (id: string, completed: boolean) => {
    try {
      // 乐观更新完成状态
      startMutating(id)
      setTodos(prev => prev.map(t => t.id === id ? { ...t, completed } : t))
      await todoService.updateTodo(id, { completed })
      await loadTodos()
    } catch (error) {
      console.error('Failed to toggle complete:', error)
      // 回滚
      setTodos(prev => prev.map(t => t.id === id ? { ...t, completed: !completed } : t))
    } finally {
      stopMutating(id)
    }
  }

  const handleResizeTodo = async (id: string, newHeight: number) => {
    try {
      const todo = todos.find(t => t.id === id)
      if (!todo) return

      // Calculate new end time based on height
      const startMinutes = timeToMinutes(todo.start_time)
      // 将像素转换为分钟：每 15 分钟对应 PIXELS_PER_15_MIN 像素
      const deltaMinutes = Math.max(15, Math.round((newHeight / PIXELS_PER_15_MIN) * 15))
      const newEndMinutes = startMinutes + deltaMinutes
      const newEndTime = minutesToTime(newEndMinutes)
      // 乐观更新结束时间
      startMutating(id)
      const original = { ...todo }
      setTodos(prev => prev.map(t => t.id === id ? { ...t, end_time: newEndTime } : t))
      await todoService.updateTodo(id, { ...todo, end_time: newEndTime })
      await loadTodos()
    } catch (error) {
      console.error('Failed to resize todo:', error)
      // 回滚
      await loadTodos()
      throw error
    } finally {
      stopMutating(id)
    }
  }

  const handleUpdateSettings = async (newSettings: Partial<Settings>) => {
    try {
      const updatedSettings = await settingsService.updateSettings(newSettings)
      setSettings(updatedSettings)
      // 成功保存后关闭设置弹窗
      setSettingsModalVisible(false)
    } catch (error) {
      console.error('Failed to update settings:', error)
      // 服务器不支持新字段（如 lunch_color）时，本地合并以确保功能可用
      setSettings(prev => ({ ...prev, ...newSettings }))
      setSettingsModalVisible(false)
    }
  }

  const handleMoveToPreviousWeek = () => {
    // Removed - no longer needed as we only show current week
  }

  const handleMoveTodo = async (todoId: string, newDayOfWeek: number, newStartTime: string) => {
    try {
      const todo = todos.find(t => t.id === todoId)
      if (!todo) return

      // Calculate new end time based on original duration
      const startMinutes = timeToMinutes(todo.start_time)
      const endMinutes = timeToMinutes(todo.end_time)
      const duration = endMinutes - startMinutes
      const newEndMinutes = timeToMinutes(newStartTime) + duration
      const newEndTime = minutesToTime(newEndMinutes)
      // 乐观移动
      startMutating(todoId)
      const original = { ...todo }
      setTodos(prev => prev.map(t => t.id === todoId ? {
        ...t,
        day_of_week: newDayOfWeek,
        start_time: newStartTime,
        end_time: newEndTime
      } : t))
      await todoService.updateTodo(todoId, {
        ...todo,
        day_of_week: newDayOfWeek,
        start_time: newStartTime,
        end_time: newEndTime
      })
      await loadTodos()
    } catch (error) {
      console.error('Failed to move todo:', error)
      // 回滚
      await loadTodos()
    } finally {
      stopMutating(todoId)
    }
  }

  const getFontSizeClass = () => {
    switch (settings.font_size) {
      case 'small': return 'font-size-small'
      case 'large': return 'font-size-large'
      default: return 'font-size-medium'
    }
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <Layout className="app-layout">
        <Header className="app-header">
          <div className="header-content">
            <Title level={3} className="app-title">
              周待办清单
            </Title>
            <SettingsButton onClick={() => setSettingsModalVisible(true)} />
          </div>
        </Header>
        <Content
          className={`app-content ${getFontSizeClass()}`}
          style={{ '--theme-color': settings.theme_color } as React.CSSProperties}
        >
          <Card variant="borderless" className="calendar-card">
      <WeeklyCalendar
        todos={todos}
        weekStart={weekStart}
        onCreateTodo={handleCreateTodo}
        onEditTodo={handleEditTodo}
        onDeleteTodo={(id) => handleDeleteTodo(id)}
        onMoveTodo={handleMoveTodo}
        onResizeTodo={handleResizeTodo}
        onToggleComplete={handleToggleComplete}
        loading={loading}
        settings={settings}
        mutatingMap={mutatingMap}
        onCalendarScroll={handleCalendarScroll}
      />
          </Card>
        </Content>
      <TodoModal
        visible={modalVisible}
        todo={selectedTodo}
        weekStart={weekStart}
        onSave={handleSaveTodo}
        onCancel={() => setModalVisible(false)}
        onDelete={handleDeleteTodo}
        position={modalPosition}
        anchorRect={modalAnchorRect}
        mutatingMap={mutatingMap}
      />
        <SettingsModal
          visible={settingsModalVisible}
          settings={settings}
          onSave={handleUpdateSettings}
          onCancel={() => setSettingsModalVisible(false)}
        />
      </Layout>
    </DndProvider>
  )
}

export default App